import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { CalendarDays, Clock, Mail, MessageCircle, Plus } from 'lucide-react';
import { z } from 'zod';
import { formatTime } from '@brimo/shared';
import { clsx } from '../../ds/clsx';
import {
  Banner,
  Button,
  Checkbox,
  Dialog,
  Icon,
  Pill,
  Progress,
  Skeleton,
  TextField,
  useToast,
  type PillVariant,
} from '../../ds/components';
import { useIsMobile } from '../../ds/hooks';
import { ApiError, errorMessage } from '../../lib/api';
import { usePermissions } from '../../lib/session';
import { relativeTime } from '../calendar/dates';
import { CaldavReconnectDialog } from './CaldavReconnectDialog';
import { SettingsPageHeader } from './SettingsFrame';
import {
  ICLOUD_CALENDAR_LABEL,
  connectionTitle,
  formatPhone,
  purposesText,
  useConnection,
  useConnections,
  useCreateCaldavConnection,
  useCreateImapConnection,
  useDeleteConnection,
  useLinkConnection,
  useReconnectConnection,
  useSupportMailbox,
  useWhatsappStatus,
  type CaldavBody,
  type ConnectionItem,
  type ConnectionStatus,
  type ImapBody,
} from './connections';
import styles from './ConnectionsSettingsPage.module.css';

/** E05 options: hosted (Google/Microsoft), the IMAP form, the two CalDAV presets (P-47, D-56). */
export type Provider = 'google' | 'outlook' | 'imap' | 'icloud' | 'caldav';

const PROVIDERS: readonly Provider[] = ['google', 'outlook', 'imap', 'icloud', 'caldav'];

function isProvider(value: string | null): value is Provider {
  return value !== null && (PROVIDERS as readonly string[]).includes(value);
}

interface ProviderOption {
  key: Provider;
  glyph: ReactNode;
  title: string;
  text: string;
}

/** E05 in the designed order; Microsoft/Outlook is the addition of D-52 (Unipile offers it). */
const providerOptions: readonly ProviderOption[] = [
  {
    key: 'google',
    glyph: 'G',
    title: 'Google Workspace',
    text: 'Mail und Kalender in einem Schritt · empfohlen',
  },
  { key: 'outlook', glyph: 'M', title: 'Microsoft / Outlook', text: 'Mail und Kalender über Microsoft 365' },
  {
    key: 'imap',
    glyph: <Icon icon={Mail} size={16} />,
    title: 'IMAP / SMTP manuell',
    text: 'Für eigene Mailserver · Server und Ports nötig',
  },
  {
    key: 'icloud',
    glyph: '◉',
    title: ICLOUD_CALENDAR_LABEL,
    text: 'App-spezifisches Passwort erforderlich',
  },
  { key: 'caldav', glyph: '⌘', title: 'CalDAV', text: 'Nextcloud, Zimbra und andere CalDAV-Server' },
];

export function pillVariantFor(status: ConnectionStatus): PillVariant {
  switch (status) {
    case 'connected':
      return 'success';
    case 'connecting':
      return 'info';
    case 'credentials_required':
      return 'warning';
    case 'disconnected':
    case 'error':
      return 'danger';
  }
}

/**
 * „Postfach und Kalender · letzter Abgleich vor 2 Minuten" (E04) or the connection's error / progress line. A
 * connected account can carry an error too: the mailbox whose calendars Unipile refuses (D-158).
 */
export function connectionSubtitle(item: ConnectionItem, now: Date = new Date()): string {
  if (item.status === 'connecting') return item.progressText ?? 'Verbindung wird aufgebaut';
  if (item.error) return item.error;
  const parts = [purposesText(item.purposes)];
  if (item.lastSyncedAt) parts.push(`letzter Abgleich ${relativeTime(item.lastSyncedAt, now)}`);
  return parts.join(' · ');
}

/**
 * EM3: the mobile card of an account that needs a new login explains the consequence. Calendar accounts
 * (Apple iCloud, CalDAV, calendar-only hosted accounts) use the sentence of the mobile screen verbatim.
 */
export function mobileLoginNote(item: ConnectionItem): string {
  const calendarOnly = item.purposes.includes('calendar') && !item.purposes.includes('mail');
  if (calendarOnly && item.status === 'credentials_required') {
    return 'Zugriff abgelaufen. Bitte erneut anmelden, sonst fehlen private Termine in der Verfügbarkeit.';
  }
  const consequence = item.purposes.includes('calendar')
    ? 'private Termine in der Verfügbarkeit'
    : 'neue Nachrichten im Posteingang';
  const reason = (item.error ?? item.statusLabel).replace(/[.\s]+$/, '');
  return `${reason}. Bitte erneut anmelden, sonst fehlen ${consequence}.`;
}

/** Whether E04 offers „Neu anmelden": hosted accounts on every broken state, CalDAV when the password is refused. */
export function offersReconnect(item: ConnectionItem): boolean {
  if (item.provider === 'google' || item.provider === 'outlook') {
    return (
      item.status === 'credentials_required' || item.status === 'disconnected' || item.status === 'error'
    );
  }
  if (item.provider === 'caldav') return item.status === 'credentials_required' || item.status === 'error';
  return false;
}

function ConnectionGlyph({ item }: { item: ConnectionItem }) {
  const tone =
    item.status === 'credentials_required'
      ? styles.glyphWarning
      : item.status === 'connected' || item.status === 'connecting'
        ? styles.glyphAccent
        : styles.glyphQuiet;
  const icon =
    item.status === 'credentials_required'
      ? Clock
      : item.provider === 'whatsapp'
        ? MessageCircle
        : item.purposes.includes('mail')
          ? Mail
          : CalendarDays;
  return (
    <span className={clsx(styles.glyph, tone)} aria-hidden="true">
      <Icon icon={icon} size={16} />
    </span>
  );
}

interface CardProps {
  item: ConnectionItem;
  mobile: boolean;
  onReconnect: (item: ConnectionItem) => void;
  onDelete: (item: ConnectionItem) => void;
  onProgress: (item: ConnectionItem) => void;
  busy: boolean;
}

/** E04 row / EM3 card of one connection with the state-specific actions. */
function ConnectionCard({ item, mobile, onReconnect, onDelete, onProgress, busy }: CardProps) {
  const needsLogin =
    item.status === 'credentials_required' || item.status === 'disconnected' || item.status === 'error';
  const reconnect = offersReconnect(item);
  const subtitle = connectionSubtitle(item);
  const pill = (
    <Pill
      variant={pillVariantFor(item.status)}
      size={mobile ? 'default' : 'large'}
      dot={item.status === 'connected'}
    >
      {item.statusLabel}
    </Pill>
  );
  if (mobile) {
    return (
      <div className={clsx(styles.mobileCard, item.status === 'credentials_required' && styles.cardWarning)}>
        <div className={styles.mobileHead}>
          <ConnectionGlyph item={item} />
          <span className={styles.mobileText}>
            <span className={styles.mobileTitle}>{item.displayName ?? item.provider}</span>
            {item.identifier ? <span className={styles.mobileSub}>{item.identifier}</span> : null}
          </span>
          {!needsLogin ? <span className={styles.mobilePill}>{pill}</span> : null}
        </div>
        <span className={clsx(styles.mobileNote, needsLogin && styles.mobileNoteStrong)}>
          {needsLogin ? mobileLoginNote(item) : subtitle}
        </span>
        {item.status === 'connecting' ? (
          <Button variant="secondary" size="large" onClick={() => onProgress(item)}>
            Fortschritt anzeigen
          </Button>
        ) : null}
        {reconnect ? (
          <Button size="large" loading={busy} onClick={() => onReconnect(item)}>
            Neu anmelden
          </Button>
        ) : null}
        <Button variant="ghost" size="large" onClick={() => onDelete(item)}>
          Trennen
        </Button>
      </div>
    );
  }
  return (
    <div className={clsx(styles.card, item.status === 'credentials_required' && styles.cardWarning)}>
      <ConnectionGlyph item={item} />
      <span className={styles.cardText}>
        <span className={styles.cardTitle}>{connectionTitle(item)}</span>
        <span className={styles.cardSub}>{subtitle}</span>
      </span>
      {pill}
      <span className={styles.cardActions}>
        {item.status === 'connecting' ? (
          <Button variant="secondary" size="small" className={styles.action} onClick={() => onProgress(item)}>
            Fortschritt anzeigen
          </Button>
        ) : null}
        {reconnect ? (
          <Button size="small" className={styles.action} loading={busy} onClick={() => onReconnect(item)}>
            Neu anmelden
          </Button>
        ) : null}
        <Button variant="ghost" size="small" className={styles.action} onClick={() => onDelete(item)}>
          Trennen
        </Button>
      </span>
    </div>
  );
}

/** E04 third card: the WhatsApp session (state from `GET /connections/whatsapp`, bridge states of D-55). */
function WhatsappCard({ mobile }: { mobile: boolean }) {
  const navigate = useNavigate();
  const status = useWhatsappStatus();
  const state = status.data?.state ?? 'none';
  const connected = state === 'connected';
  const subtitle = connected
    ? `${status.data?.phoneE164 ? `${formatPhone(status.data.phoneE164)} · ` : ''}Session aktiv`
    : state === 'disconnected'
      ? 'Session unterbrochen · Nachrichten warten im Ausgang'
      : state === 'starting' || state === 'qr' || state === 'expired' || state === 'pairing'
        ? 'Session wird aufgebaut'
        : 'Keine Session offen · QR-Code mit WhatsApp scannen';
  const pill = (
    <Pill
      variant={connected ? 'success' : state === 'disconnected' ? 'danger' : 'neutral'}
      size={mobile ? 'default' : 'large'}
      dot={connected}
    >
      {connected ? 'Verbunden' : state === 'disconnected' ? 'Getrennt' : 'Nicht verbunden'}
    </Pill>
  );
  const glyph = (
    <span
      className={clsx(styles.glyph, connected ? styles.glyphAccent : styles.glyphQuiet)}
      aria-hidden="true"
    >
      <Icon icon={MessageCircle} size={16} />
    </span>
  );
  const action = connected || state === 'disconnected' ? 'Session verwalten' : 'QR-Code anzeigen';
  if (mobile) {
    return (
      <div className={styles.mobileCard}>
        <div className={styles.mobileHead}>
          {glyph}
          <span className={styles.mobileText}>
            <span className={styles.mobileTitle}>WhatsApp</span>
            <span className={styles.mobileSub}>{status.isPending ? <Skeleton width={90} /> : subtitle}</span>
          </span>
          {connected ? <span className={styles.mobilePill}>{pill}</span> : null}
        </div>
        <Button variant="secondary" size="large" onClick={() => navigate('/settings/whatsapp')}>
          {action}
        </Button>
      </div>
    );
  }
  return (
    <div className={styles.card}>
      {glyph}
      <span className={styles.cardText}>
        <span className={styles.cardTitle}>WhatsApp</span>
        <span className={styles.cardSub}>{status.isPending ? <Skeleton width={160} /> : subtitle}</span>
      </span>
      {pill}
      <span className={styles.cardActions}>
        <Button
          size="small"
          className={styles.action}
          variant={connected ? 'secondary' : 'primary'}
          onClick={() => navigate('/settings/whatsapp')}
        >
          {action}
        </Button>
      </span>
    </div>
  );
}

// --- provider chooser (E05), IMAP form (E06) and CalDAV form (step 2, D-56) ------------------------------

const imapSchema = z.object({
  imapHost: z.string().trim().min(1, 'Pflichtangabe.'),
  imapPort: z.string().trim().regex(/^\d+$/, 'Bitte eine Portnummer eingeben.'),
  smtpHost: z.string().trim().min(1, 'Pflichtangabe.'),
  smtpPort: z.string().trim().regex(/^\d+$/, 'Bitte eine Portnummer eingeben.'),
  email: z.string().trim().min(1, 'Pflichtangabe.').email('Bitte prüfen Sie die E-Mail-Adresse.'),
  password: z.string().min(1, 'Pflichtangabe.'),
  ssl: z.boolean(),
});

type ImapValues = z.infer<typeof imapSchema>;

const imapDefaults: ImapValues = {
  imapHost: '',
  imapPort: '993',
  smtpHost: '',
  smtpPort: '587',
  email: '',
  password: '',
  ssl: true,
};

/** The server address is required for a generic CalDAV server; the iCloud preset fixes it on the server. */
const caldavSchema = z
  .object({
    preset: z.enum(['caldav', 'icloud']),
    serverUrl: z.string().trim(),
    username: z.string().trim().min(1, 'Pflichtangabe.'),
    password: z.string().min(1, 'Pflichtangabe.'),
  })
  .superRefine((values, context) => {
    if (values.preset === 'caldav' && values.serverUrl.length === 0) {
      context.addIssue({ code: 'custom', path: ['serverUrl'], message: 'Pflichtangabe.' });
    }
  });

type CaldavValues = z.infer<typeof caldavSchema>;

function caldavDefaults(preset: 'caldav' | 'icloud'): CaldavValues {
  return { preset, serverUrl: '', username: '', password: '' };
}

const ICLOUD_PASSWORD_HELP =
  'Erstellen Sie das Passwort unter appleid.apple.com › Anmeldung und Sicherheit › App-spezifische Passwörter.';

export interface AddDialogProps {
  open: boolean;
  support: boolean;
  /** Preselected option (Beratung 08 „Verbinden" of the CalDAV row opens the chooser with CalDAV). */
  initialProvider: Provider;
  onClose: () => void;
  onCreated: (item: ConnectionItem) => void;
}

/** E05 (provider) → hosted auth redirect, E06 (IMAP form) or the CalDAV form as step 2 of 2. */
export function AddConnectionDialog({ open, support, initialProvider, onClose, onCreated }: AddDialogProps) {
  const [provider, setProvider] = useState<Provider>(initialProvider);
  const [step, setStep] = useState<1 | 2>(1);
  const [error, setError] = useState<string | null>(null);
  const link = useLinkConnection();
  const createImap = useCreateImapConnection();
  const createCaldav = useCreateCaldavConnection();
  const imapForm = useForm<ImapValues>({ resolver: zodResolver(imapSchema), defaultValues: imapDefaults });
  const caldavForm = useForm<CaldavValues>({
    resolver: zodResolver(caldavSchema),
    defaultValues: caldavDefaults('caldav'),
  });
  // The support mailbox is a mailbox: the calendar-only presets are not offered for it.
  const options = support
    ? providerOptions.filter((option) => option.key !== 'icloud' && option.key !== 'caldav')
    : providerOptions;

  useEffect(() => {
    if (open) {
      setProvider(initialProvider);
      setStep(1);
      setError(null);
      link.reset();
      createImap.reset();
      createCaldav.reset();
    }
    // Reset once per opening.
  }, [open]);

  async function next() {
    setError(null);
    if (provider === 'google' || provider === 'outlook') {
      try {
        const result = await link.mutateAsync({ provider, purposes: ['mail', 'calendar'], support });
        window.location.assign(result.url);
      } catch (failure) {
        setError(errorMessage(failure));
      }
      return;
    }
    // Step 2 starts clean, also after a failed attempt with another preset („Erneut prüfen" → „Verbinden").
    createImap.reset();
    createCaldav.reset();
    if (provider === 'imap') imapForm.reset(imapDefaults);
    else caldavForm.reset(caldavDefaults(provider));
    setStep(2);
  }

  /** Field errors of a 400 go under the fields, the message (also of a 409) into the box at the top. */
  function showFailure<Values extends Record<string, unknown>>(
    failure: unknown,
    values: Values,
    setFieldError: (name: keyof Values & string, error: { message: string }) => void,
  ) {
    if (failure instanceof ApiError) {
      for (const [field, message] of Object.entries(failure.fields)) {
        if (field in values) setFieldError(field, { message });
      }
      setError(failure.message);
    } else {
      setError(errorMessage(failure));
    }
  }

  const submitImap = imapForm.handleSubmit(async (values) => {
    setError(null);
    const body: ImapBody = {
      preset: 'imap',
      email: values.email,
      imapHost: values.imapHost,
      imapPort: Number(values.imapPort),
      password: values.password,
      smtpHost: values.smtpHost,
      smtpPort: Number(values.smtpPort),
      ssl: values.ssl,
    };
    try {
      const created = await createImap.mutateAsync({ body, support });
      onCreated(created);
    } catch (failure) {
      showFailure(failure, values, imapForm.setError);
    }
  });

  const submitCaldav = caldavForm.handleSubmit(async (values) => {
    setError(null);
    const body: CaldavBody =
      values.preset === 'icloud'
        ? { preset: 'icloud', username: values.username, password: values.password }
        : {
            preset: 'caldav',
            serverUrl: values.serverUrl,
            username: values.username,
            password: values.password,
          };
    try {
      const created = await createCaldav.mutateAsync(body);
      onCreated(created);
    } catch (failure) {
      showFailure(failure, values, caldavForm.setError);
    }
  });

  const nextLabel =
    provider === 'google' ? 'Weiter zu Google' : provider === 'outlook' ? 'Weiter zu Microsoft' : 'Weiter';

  if (step === 2 && provider === 'imap') {
    const formId = 'imap-form';
    const { register, control, formState } = imapForm;
    const fieldMessage = (key: keyof ImapValues): string | undefined => formState.errors[key]?.message;
    return (
      <Dialog
        key="imap"
        open={open}
        onClose={onClose}
        eyebrow="Verbindung hinzufügen · Schritt 2 von 2"
        title="IMAP und SMTP"
        width={640}
        footer={
          <>
            <Button variant="secondary" onClick={() => setStep(1)}>
              Zurück
            </Button>
            <Button type="submit" form={formId} loading={createImap.isPending}>
              {createImap.isError ? 'Erneut prüfen' : 'Verbinden'}
            </Button>
          </>
        }
      >
        <form id={formId} className={styles.imapForm} onSubmit={(event) => void submitImap(event)} noValidate>
          {error ? <Banner tone="danger">{error}</Banner> : null}
          <div className={styles.imapGrid}>
            <TextField
              label="IMAP-Server"
              autoComplete="off"
              invalid={Boolean(fieldMessage('imapHost'))}
              hint={fieldMessage('imapHost')}
              {...register('imapHost')}
            />
            <TextField
              label="Port"
              inputMode="numeric"
              error={fieldMessage('imapPort')}
              {...register('imapPort')}
            />
            <TextField
              label="SMTP-Server"
              autoComplete="off"
              invalid={Boolean(fieldMessage('smtpHost'))}
              hint={fieldMessage('smtpHost')}
              {...register('smtpHost')}
            />
            <TextField
              label="Port"
              inputMode="numeric"
              error={fieldMessage('smtpPort')}
              {...register('smtpPort')}
            />
            <TextField
              label="Nutzername"
              type="email"
              autoComplete="username"
              error={fieldMessage('email')}
              {...register('email')}
            />
            <TextField
              label="Passwort"
              type="password"
              autoComplete="current-password"
              invalid={Boolean(fieldMessage('password'))}
              hint={fieldMessage('password')}
              {...register('password')}
            />
          </div>
          <div className={styles.sslRow}>
            <Controller
              control={control}
              name="ssl"
              render={({ field }) => (
                <Checkbox
                  label="SSL/TLS verwenden"
                  checked={field.value}
                  onChange={(event) => field.onChange(event.target.checked)}
                />
              )}
            />
          </div>
        </form>
      </Dialog>
    );
  }

  if (step === 2 && (provider === 'icloud' || provider === 'caldav')) {
    const formId = 'caldav-form';
    const icloud = provider === 'icloud';
    const { register, formState } = caldavForm;
    const fieldMessage = (key: keyof CaldavValues): string | undefined => formState.errors[key]?.message;
    return (
      <Dialog
        key="caldav"
        open={open}
        onClose={onClose}
        eyebrow="Verbindung hinzufügen · Schritt 2 von 2"
        title={icloud ? ICLOUD_CALENDAR_LABEL : 'CalDAV'}
        width={640}
        footer={
          <>
            <Button variant="secondary" onClick={() => setStep(1)}>
              Zurück
            </Button>
            <Button type="submit" form={formId} loading={createCaldav.isPending}>
              {createCaldav.isError ? 'Erneut prüfen' : 'Verbinden'}
            </Button>
          </>
        }
      >
        <form
          id={formId}
          className={styles.imapForm}
          onSubmit={(event) => void submitCaldav(event)}
          noValidate
        >
          {error ? <Banner tone="danger">{error}</Banner> : null}
          <div className={styles.caldavGrid}>
            {icloud ? null : (
              <TextField
                label="Server-Adresse"
                type="url"
                autoComplete="off"
                placeholder="https://cloud.example.de/remote.php/dav"
                error={fieldMessage('serverUrl')}
                {...register('serverUrl')}
              />
            )}
            <TextField
              label={icloud ? 'Apple-ID' : 'Nutzername'}
              type={icloud ? 'email' : 'text'}
              autoComplete="username"
              error={fieldMessage('username')}
              {...register('username')}
            />
            <TextField
              label={icloud ? 'App-spezifisches Passwort' : 'Passwort'}
              type="password"
              autoComplete="current-password"
              error={fieldMessage('password')}
              {...register('password')}
            />
          </div>
          {icloud ? <div className={styles.helpBox}>{ICLOUD_PASSWORD_HELP}</div> : null}
        </form>
      </Dialog>
    );
  }

  // Distinct keys: a re-used footer button would carry the click's default action (submit) into step 2.
  return (
    <Dialog
      key="provider"
      open={open}
      onClose={onClose}
      eyebrow={support ? 'Support-Postfach verbinden' : 'Verbindung hinzufügen'}
      title={support ? 'Woher sollen die Anfragen kommen?' : 'Woher sollen Mails und Termine kommen?'}
      width={600}
      footerNote="Sie können später weitere Konten verbinden."
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Abbrechen
          </Button>
          <Button loading={link.isPending} onClick={() => void next()}>
            {nextLabel}
          </Button>
        </>
      }
    >
      {error ? <Banner tone="danger">{error}</Banner> : null}
      <div className={styles.providerList} role="radiogroup" aria-label="Anbieter">
        {options.map((option) => {
          const selected = option.key === provider;
          return (
            <button
              key={option.key}
              type="button"
              role="radio"
              aria-checked={selected}
              className={clsx(styles.provider, selected && styles.providerSelected)}
              onClick={() => setProvider(option.key)}
            >
              <span
                className={clsx(styles.providerGlyph, selected && styles.providerGlyphSelected)}
                aria-hidden="true"
              >
                {option.glyph}
              </span>
              <span className={styles.providerText}>
                <span className={styles.providerTitle}>{option.title}</span>
                <span className={styles.providerSub}>{option.text}</span>
              </span>
              {selected ? (
                <span className={styles.providerCheck} aria-hidden="true">
                  ✓
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
    </Dialog>
  );
}

// --- progress (E07) ---------------------------------------------------------------------------------------

interface Step {
  label: string;
  state: 'done' | 'active' | 'open';
}

/** The checklist of E07 from `progressText`/`status` (D-37: no history sync, hence no mail counter). */
export function progressSteps(item: ConnectionItem): Step[] {
  const text = item.progressText ?? '';
  const connected = item.status === 'connected';
  const accessDone = connected || text.startsWith('Zugriff bestätigt');
  const calendarMatch = /(\d+) Kalender gefunden/.exec(text);
  const steps: Step[] = [{ label: 'Zugriff bestätigt', state: accessDone ? 'done' : 'active' }];
  if (item.purposes.includes('calendar')) {
    const count = calendarMatch ? Number(calendarMatch[1]) : connected ? item.calendars.length : null;
    steps.push({
      label: count === null ? 'Kalender lesen' : `Kalender gelesen · ${count} Kalender gefunden`,
      state: count !== null ? 'done' : accessDone ? 'active' : 'open',
    });
  }
  if (item.purposes.includes('mail')) {
    const calendarPending = steps.some((step) => step.state !== 'done');
    steps.push({
      label: connected ? 'Postfach verbunden' : 'Postfach wird verbunden',
      state: connected ? 'done' : calendarPending ? 'open' : 'active',
    });
  }
  return steps;
}

function ProgressCard({ id, onClose }: { id: string; onClose: () => void }) {
  const connection = useConnection(id);
  const startedAt = useMemo(() => new Date(), []);
  const item = connection.data;
  const steps = item ? progressSteps(item) : [];
  const done = steps.filter((step) => step.state === 'done').length;
  const percent = steps.length === 0 ? 0 : Math.round((done / steps.length) * 100);
  const finished = item?.status === 'connected';
  const failed =
    item !== undefined &&
    (item.status === 'error' || item.status === 'disconnected' || item.status === 'credentials_required');
  return (
    <Dialog
      open
      onClose={onClose}
      eyebrow={item?.displayName ?? 'Verbindung'}
      title={finished ? 'Konto verbunden' : failed ? 'Verbindung fehlgeschlagen' : 'Konto wird verbunden'}
      subtitle={
        finished
          ? 'Der erste Abgleich läuft im Hintergrund.'
          : failed
            ? (item?.error ?? 'Die Verbindung konnte nicht hergestellt werden.')
            : 'Das kann bei großen Postfächern einige Minuten dauern. Sie können die Seite verlassen.'
      }
      width={560}
      closeOnScrim={false}
      footerNote={`Gestartet ${formatTime(startedAt)}`}
      footer={
        <Button variant="secondary" onClick={onClose}>
          {finished || failed ? 'Schließen' : 'Im Hintergrund fortsetzen'}
        </Button>
      }
    >
      {connection.isError ? <Banner tone="danger">{errorMessage(connection.error)}</Banner> : null}
      {connection.isPending ? (
        <Skeleton variant="block" height={120} />
      ) : (
        <>
          <Progress value={finished ? 100 : percent} label="Verbindungsaufbau" />
          <ul className={styles.progressList}>
            {steps.map((step) => (
              <li key={step.label} className={clsx(styles.progressStep, styles[`step-${step.state}`])}>
                <span className={styles.progressMark} aria-hidden="true">
                  {step.state === 'done' ? '✓' : ''}
                </span>
                {step.label}
              </li>
            ))}
          </ul>
        </>
      )}
    </Dialog>
  );
}

// --- page --------------------------------------------------------------------------------------------------

interface AddState {
  support: boolean;
  provider: Provider;
}

/** `/settings/connections` — Einstellungen 04–07 (EM3 on mobile). */
export function ConnectionsSettingsPage() {
  const mobile = useIsMobile();
  const permissions = usePermissions();
  const { toast } = useToast();
  const [params, setParams] = useSearchParams();
  const connections = useConnections();
  const admin = permissions.has('settings.organization');
  const support = useSupportMailbox(admin);
  const reconnect = useReconnectConnection();
  const remove = useDeleteConnection();
  const [addOpen, setAddOpen] = useState<AddState | null>(null);
  const [reconnectItem, setReconnectItem] = useState<ConnectionItem | null>(null);
  const [progressId, setProgressId] = useState<string | null>(null);
  const [failedId, setFailedId] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<ConnectionItem | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const connected = params.get('connected');
    const failed = params.get('failed');
    const add = params.get('add');
    if (!connected && !failed && !add) return;
    if (connected) setProgressId(connected);
    if (failed) setFailedId(failed);
    // Beratung 08 „Verbinden" of the CalDAV row: `?add=caldav` opens the chooser with that option.
    if (add) setAddOpen({ support: false, provider: isProvider(add) ? add : 'google' });
    setParams({}, { replace: true });
  }, [params, setParams]);

  const items = connections.data?.items ?? [];
  const personal = items.filter((item) => !item.isSupportMailbox && item.provider !== 'whatsapp');
  const failedItem = failedId ? items.find((item) => item.id === failedId) : undefined;

  async function reauth(item: ConnectionItem) {
    if (item.provider === 'caldav') {
      setReconnectItem(item);
      return;
    }
    setError(null);
    try {
      const result = await reconnect.mutateAsync(item.id);
      window.location.assign(result.url);
    } catch (failure) {
      setError(errorMessage(failure));
    }
  }

  async function disconnect() {
    if (!confirm) return;
    try {
      await remove.mutateAsync(confirm.id);
      toast({ message: 'Verbindung getrennt.' });
    } catch (failure) {
      setError(errorMessage(failure));
    } finally {
      setConfirm(null);
    }
  }

  const list = (entries: ConnectionItem[]) =>
    entries.map((item) => (
      <ConnectionCard
        key={item.id}
        item={item}
        mobile={mobile}
        busy={reconnect.isPending}
        onReconnect={(entry) => void reauth(entry)}
        onDelete={setConfirm}
        onProgress={(entry) => setProgressId(entry.id)}
      />
    ));

  return (
    <div className={styles.page}>
      <SettingsPageHeader
        eyebrow="Meine Einstellungen"
        title="Verbindungen"
        description="Postfach, Kalender und WhatsApp-Session. DAMOKLES liest nur, was Sie hier freigeben."
      />
      {error ? <Banner tone="danger">{error}</Banner> : null}
      {failedItem || failedId ? (
        <Banner tone="danger" title="Die Verbindung konnte nicht hergestellt werden.">
          {failedItem?.error ?? 'Bitte versuchen Sie es erneut.'}
        </Banner>
      ) : null}
      {connections.isError ? (
        <Banner
          tone="danger"
          actions={
            <Button variant="secondary" size="small" onClick={() => void connections.refetch()}>
              Erneut laden
            </Button>
          }
        >
          {errorMessage(connections.error)}
        </Banner>
      ) : null}

      <div className={styles.list}>
        {connections.isPending ? (
          <>
            <Skeleton variant="block" height={76} />
            <Skeleton variant="block" height={76} />
            <Skeleton variant="block" height={76} />
          </>
        ) : (
          list(personal)
        )}
        <WhatsappCard mobile={mobile} />
        {mobile ? (
          <button
            type="button"
            className={styles.addMobile}
            onClick={() => setAddOpen({ support: false, provider: 'google' })}
          >
            <span>Weiteres Konto verbinden</span>
            <span className={styles.addMobileGlyph} aria-hidden="true">
              ＋
            </span>
          </button>
        ) : (
          <div className={styles.addCard}>
            <span className={clsx(styles.glyph, styles.glyphSunken)} aria-hidden="true">
              <Icon icon={Plus} size={16} />
            </span>
            <span className={styles.cardText}>
              <span className={styles.addTitle}>Weiteres Postfach oder Kalender verbinden</span>
              <span className={styles.addSub}>Google Workspace · IMAP/SMTP · Apple iCloud · CalDAV</span>
            </span>
            <Button
              variant="secondary"
              className={styles.addButton}
              onClick={() => setAddOpen({ support: false, provider: 'google' })}
            >
              Verbindung hinzufügen
            </Button>
          </div>
        )}
      </div>

      {admin ? (
        <section className={styles.supportSection} aria-labelledby="support-title">
          <h2 id="support-title" className={styles.supportTitle}>
            Support-Postfach der Organisation
          </h2>
          <p className={styles.supportText}>
            Anfragen an dieses Postfach landen in den Tickets. Ist es getrennt, erreichen neue Anfragen
            niemanden.
          </p>
          {support.isError ? <Banner tone="danger">{errorMessage(support.error)}</Banner> : null}
          {support.isPending ? (
            <Skeleton variant="block" height={76} />
          ) : support.data ? (
            <div className={styles.list}>{list([support.data])}</div>
          ) : (
            <div className={styles.addCard}>
              <span className={clsx(styles.glyph, styles.glyphSunken)} aria-hidden="true">
                <Icon icon={Mail} size={16} />
              </span>
              <span className={styles.cardText}>
                <span className={styles.addTitle}>Support-Postfach verbinden</span>
                <span className={styles.addSub}>Google Workspace · Microsoft / Outlook · IMAP/SMTP</span>
              </span>
              <Button
                variant="secondary"
                className={styles.addButton}
                onClick={() => setAddOpen({ support: true, provider: 'google' })}
              >
                Postfach verbinden
              </Button>
            </div>
          )}
        </section>
      ) : null}

      {!mobile ? (
        <div className={styles.footnote}>
          Der Versand über die Firmennummer und WhatsApp-Vorlagen werden in der Organisation eingerichtet.{' '}
          {admin ? (
            <Link to="/settings/organization/telephony">Telefonie und WhatsApp-Versand</Link>
          ) : (
            <span>Telefonie und WhatsApp-Versand</span>
          )}
        </div>
      ) : null}

      <AddConnectionDialog
        open={addOpen !== null}
        support={addOpen?.support ?? false}
        initialProvider={addOpen?.provider ?? 'google'}
        onClose={() => setAddOpen(null)}
        onCreated={(item) => {
          setAddOpen(null);
          if (item.status === 'connecting') {
            toast({ message: 'Verbindung angelegt.' });
            setProgressId(item.id);
          } else {
            toast({ message: 'Verbindung hergestellt.' });
          }
        }}
      />
      <CaldavReconnectDialog
        item={reconnectItem}
        onClose={() => setReconnectItem(null)}
        onDone={() => {
          setReconnectItem(null);
          toast({ message: 'Verbindung hergestellt.' });
        }}
      />
      {progressId ? <ProgressCard id={progressId} onClose={() => setProgressId(null)} /> : null}
      <Dialog
        open={confirm !== null}
        onClose={() => setConfirm(null)}
        eyebrow="Verbindungen"
        title="Verbindung trennen?"
        width={560}
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirm(null)}>
              Abbrechen
            </Button>
            <Button variant="destructiveFilled" loading={remove.isPending} onClick={() => void disconnect()}>
              Trennen
            </Button>
          </>
        }
      >
        <p className={styles.dialogText}>
          {confirm ? connectionTitle(confirm) : ''} wird getrennt. DAMOKLES liest dieses Konto danach nicht
          mehr; bereits abgelegte Nachrichten bleiben in den Kontaktakten.
        </p>
      </Dialog>
    </div>
  );
}
